/**
 * Postal-code validation — single source of truth shared by the frontend inputs
 * and the backend route guards / auth schema. Accepts both US ZIP codes and
 * Canadian postal codes so dancers and studios in Canada can see pricing and
 * register (SDA-197). Distance + pricing-tier math is coordinate-based and
 * unchanged; this layer only governs what counts as a valid code.
 */
/** US ZIP — 5 digits, optional +4 (e.g. `10001`, `10001-2345`). */
export const US_ZIP_REGEX = /^\d{5}(-\d{4})?$/;
/**
 * Canadian postal code — `A1A 1A1` (letter-digit-letter, optional space or
 * hyphen, digit-letter-digit). Case-insensitive; separator optional so
 * `K1C6E7` and `K1C 6E7` both match.
 */
export const CA_POSTAL_REGEX = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
/** True when the value is a valid US ZIP or Canadian postal code. */
export function isValidPostalCode(value) {
    const trimmed = (value ?? '').trim();
    return US_ZIP_REGEX.test(trimmed) || CA_POSTAL_REGEX.test(trimmed);
}
/**
 * Detect which country a postal code belongs to. US (all-digit) and CA
 * (leading letter) formats don't overlap, so this is unambiguous. Returns
 * null for anything that isn't a valid US or CA code.
 */
export function detectPostalCountry(value) {
    const trimmed = (value ?? '').trim();
    if (US_ZIP_REGEX.test(trimmed))
        return 'US';
    if (CA_POSTAL_REGEX.test(trimmed))
        return 'CA';
    return null;
}
/**
 * Canonicalize a postal code: trim + uppercase, and for Canadian codes insert
 * the single canonical space (`K1C6E7` / `K1C-6E7` → `K1C 6E7`). US codes are
 * returned trimmed/uppercased (uppercase is a no-op for digits). Unknown input
 * is returned trimmed/uppercased unchanged so callers can still pass it through.
 */
export function normalizePostalCode(value) {
    const trimmed = (value ?? '').trim().toUpperCase();
    if (detectPostalCountry(trimmed) === 'CA') {
        const compact = trimmed.replace(/[ -]/g, '');
        return `${compact.slice(0, 3)} ${compact.slice(3)}`;
    }
    return trimmed;
}
//# sourceMappingURL=location.js.map